import LoadingSkeleton from "@shared/LoadingSkeleton";
import { displayCount } from "@utils/utils";
import ReactCountryFlag from "react-country-flag";
import ReactTooltip from "react-tooltip";

const getMatchScore = (score) => {
  const colors = score > 70 ? "tw-bg-[#f6fef9] tw-border-[#039855]" : "tw-bg-[#fffdf5] tw-border-[#fcca3d]";
  return <span className={`${colors} tw-border tw-rounded-2xl tw-text-xs tw-text-gray-900 tw-px-1.5 tw-w-fit tw-truncate`}>Match Score - {score}%</span>;
};

const UserCompanyProfileCardView = ({ tableData = [], selectedCompany, onToggleProfile, userCompanyProfilesFetched, showCompanySearchInput = false, companySearchText = "", onCompanySearchTextChange, onSearchCompanyProfiles }) => {
  if (!userCompanyProfilesFetched && !showCompanySearchInput) {
    return (
      <div>
        <LoadingSkeleton lines={5} lineHeight="135px" width="100%" shape="rectangle" />
      </div>
    );
  }

  return (
    <div className="tw-space-y-3">
      <ReactTooltip className="trademo-tooltip" backgroundColor="#8C8C8C" textColor="#FFFFFF" place="top" id="actions" />

      <div className="tw-text-gray-900 tw-font-semibold tw-capitalize tw-text-sm">Select your company Profile</div>
      <div className="tw-text-gray-500 tw-text-sm tw-mt-1">Selecting your company helps to create personalised experience for you</div>

      {showCompanySearchInput ? (
        <div className="tw-flex tw-gap-2 tw-items-center tw-text-sm tw-mt-2">
          <div className="tw-relative tw-w-full">
            <input
              type="text"
              placeholder="Search company name"
              className="tw-border tw-border-gray-200 tw-bg-white tw-rounded-lg tw-w-full tw-h-9 tw-flex tw-items-center tw-justify-between tw-pl-3 tw-pr-8 tw-font-medium"
              value={companySearchText}
              onChange={(e) => onCompanySearchTextChange?.(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && userCompanyProfilesFetched) {
                  onSearchCompanyProfiles?.();
                }
              }}
            />
            {!!companySearchText?.trim() && (
              <button type="button" className="tw-absolute tw-top-1.5 tw-right-2 tw-h-6 tw-w-6 tw-bg-white" onClick={() => onCompanySearchTextChange?.("")}>
                <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#101828">
                  <path d="M291-253.85 253.85-291l189-189-189-189L291-706.15l189 189 189-189L706.15-669l-189 189 189 189L669-253.85l-189-189-189 189Z" />
                </svg>
              </button>
            )}
          </div>
          <button disabled={!userCompanyProfilesFetched} type="button" onClick={() => onSearchCompanyProfiles?.()} className="tw-bg-primary-purple-600 disabled:!tw-opacity-50 disabled:!tw-cursor-not-allowed tw-text-sm hover:tw-bg-primary-purple-700 tw-px-5 tw-text-white tw-outline-none tw-border-none tw-rounded-lg tw-font-medium tw-h-9 tw-whitespace-nowrap">
            Search
          </button>
        </div>
      ) : (
        <div className="tw-border tw-border-gray-200 tw-bg-gray-25 tw-rounded-lg tw-w-full tw-h-9 tw-flex tw-items-center tw-justify-between tw-px-3 tw-cursor-not-allowed tw-mt-2">
          <span className="tw-text-gray-900 tw-capitalize tw-text-sm">{selectedCompany?.key}</span>
          {selectedCompany?.nameMatchPercentage && getMatchScore(selectedCompany?.nameMatchPercentage)}
        </div>
      )}

      {!userCompanyProfilesFetched ? (
        <LoadingSkeleton lines={5} lineHeight="135px" width="100%" shape="rectangle" />
      ) : (
        <div className="tw-border tw-border-[#c6f66e] tw-p-4 tw-rounded-lg tw-space-y-2.5">
          <div className="tw-text-black tw-text-xs tw-font-medium">
            {tableData.length > 0 ? (
              <>
                We've identified your company as <span className="tw-capitalize">{tableData[0]?.key}</span>. If this isn't correct, please select it from the below option.
              </>
            ) : (
              <>No company suggestions found. Please search for your correct company above and proceed to next steps</>
            )}
          </div>
          {tableData?.length > 0 && (
            <div className="tw-max-h-[500px] tw-overflow-y-auto tw-space-y-2.5">
              {tableData.map((row) => {
                const tickerData = row.stockTickerDetails ? Object.entries(row.stockTickerDetails) : [];
                const identifiers = row.company_identifiers ? row.company_identifiers : [];
                const hasIdentifiers = row.lei || (tickerData && tickerData.length > 0) || (identifiers && identifiers.length > 0) || row.ruc;
                const hasValues = (row.import_value || row.export_value) > 0;
                const isSelected = selectedCompany?.company_id === row?.company_id;

                return (
                  <div key={row.hashed_id} className={`tw-border tw-flex tw-items-start tw-gap-2 tw-cursor-pointer tw-border-gray-200 tw-p-3 tw-rounded-xl tw-bg-white ${isSelected ? "tw-border-primary-purple-500 tw-bg-primary-purple-50" : ""}`} onClick={() => onToggleProfile(row)} role="button" tabIndex={0}>
                    <input type="radio" checked={isSelected} className="!tw-size-4 tw-accent-primary-purple-600 !tw-mt-1" />
                    <div className="tw-flex-1">
                      <div className="tw-flex tw-justify-between">
                        <div className="tw-w-4/5">
                          <span className="tw-text-gray-900 tw-font-semibold tw-capitalize tw-text-sm tw-pe-2">{row.key}</span>
                        </div>
                        {row.nameMatchPercentage && getMatchScore(row.nameMatchPercentage)}
                      </div>
                      {row.company_id !== "not_selected" && (
                        <div className="tw-mt-1 tw-text-xs tw-text-gray-600 tw-capitalize tw-line-clamp-3">
                          {row.address}&nbsp;{row.state}&nbsp;
                          {row.country_code && (
                            <span className={"tw-text-gray-900"}>
                              <ReactCountryFlag svg countryCode={row.country_code} /> {row.country}
                            </span>
                          )}
                        </div>
                      )}
                      {hasIdentifiers && (
                        <div className="tw-space-y-1 tw-text-gray-900 tw-mt-1 tw-flex tw-gap-3 tw-flex-wrap">
                          {row.lei && (
                            <div className="tw-border-l-[3px] tw-ps-1 tw-border-[#185cda] tw-text-xs">
                              LEI Number:<span className="tw-font-medium tw-ps-1">{row.lei}</span>
                            </div>
                          )}
                          {tickerData.length > 0 && (
                            <div className="tw-border-l-[3px] tw-ps-1 tw-border-[#6da0f7] tw-text-xs tw-flex tw-items-center">
                              Stock Ticker:
                              <div className="tw-ms-2 tw-font-medium">{tickerData.map(([key, val]) => `${key}: ${val}`).join(", ")}</div>
                            </div>
                          )}
                          {identifiers &&
                            Object.keys(identifiers).length > 0 &&
                            Object.keys(identifiers).map((item) => {
                              const values = identifiers[item].split(",").map((v) => v.trim());
                              const firstValue = values[0];
                              const remainingCount = values.length - 1;

                              return (
                                <div key={item} className="tw-border-l-[3px] tw-ps-1 tw-border-[#6da0f7] tw-text-xs tw-flex tw-items-center tw-mt-0">
                                  {item}
                                  <div className="tw-ms-2 tw-font-medium tw-flex tw-items-center tw-gap-1">
                                    {firstValue}
                                    {remainingCount > 0 && (
                                      <span data-for={`tooltip-${item}`} data-tip={values.slice(1).join(", ")} className="green-badge">
                                        +{remainingCount}
                                      </span>
                                    )}
                                  </div>
                                  {remainingCount > 0 && <ReactTooltip id={`tooltip-${item}`} place="top" effect="solid" />}
                                </div>
                              );
                            })}
                          {row.ruc && (
                            <div className="tw-border-l-[3px] tw-ps-1 tw-border-[#bfd5f9] tw-text-xs">
                              RUC:<span className="tw-font-medium tw-ps-1">{row.ruc}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {hasValues && (
                        <div className="tw-mt-3 tw-border-dashed tw-border-t tw-border-t-gray-300 tw-pt-4 tw-flex tw-text-sm tw-font-normal tw-gap-8">
                          {row.import_value > 0 && (
                            <div className="tw-w-1/2 tw-text-xs">
                              <div className="tw-text-gray-500">Imports in Last 12 months</div>
                              <div className="tw-text-gray-900 tw-font-medium tw-tracking-[-0.14px]">USD {displayCount(row.import_value, 2)}</div>
                            </div>
                          )}
                          {row.export_value > 0 && (
                            <div className="tw-w-1/2 tw-text-xs">
                              <div className="tw-text-gray-500">Exports in Last 12 months</div>
                              <div className="tw-text-gray-900 tw-font-medium tw-tracking-[-0.14px]">USD {displayCount(row.export_value, 2)}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserCompanyProfileCardView;
